import React from 'react';

type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

type PageItem = number | 'ellipsis';

const buildPageItems = (page: number, totalPages: number): PageItem[] => {
  if (totalPages <= 1) return [1];

  const keep = new Set<number>([1, totalPages]);
  for (let p = page - 1; p <= page + 1; p += 1) {
    if (p >= 1 && p <= totalPages) keep.add(p);
  }

  const sorted = Array.from(keep).sort((a, b) => a - b);
  const items: PageItem[] = [];

  sorted.forEach((p, idx) => {
    const prev = sorted[idx - 1];
    if (idx > 0 && prev !== undefined && p - prev > 1) {
      items.push('ellipsis');
    }
    items.push(p);
  });

  return items;
};

const Pagination: React.FC<PaginationProps> = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const items = buildPageItems(page, totalPages);

  return (
    <div className="app-pagination">
      <button
        className="btn-secondary page-btn"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        type="button"
      >
        Prev
      </button>
      <div className="page-numbers">
        {items.map((item, idx) => {
          if (item === 'ellipsis') {
            return (
              <span key={`ellipsis-${idx}`} className="page-ellipsis">
                ...
              </span>
            );
          }

          const isActive = item === page;
          return (
            <button
              key={`page-${item}`}
              className={`btn-secondary page-btn ${isActive ? 'active' : ''}`}
              onClick={() => {
                if (!isActive) onPageChange(item);
              }}
              type="button"
              aria-current={isActive ? 'page' : undefined}
            >
              {item}
            </button>
          );
        })}
      </div>
      <button
        className="btn-secondary page-btn"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        type="button"
      >
        Next
      </button>
      <div className="page-info">Page {page} of {totalPages}</div>
    </div>
  );
};

export default Pagination;
